import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { map, Observable } from 'rxjs'
import { EnvironmentFanOutService, EnvironmentResult } from '../../multi-environment/environment-fan-out.service'
import { FanOut, tagRows, toFanOut } from '../../multi-environment/environment-merge'
import { isSelfEnvironment, SupportEnvironment } from '../../multi-environment/support-environment'
import { injectMutation, injectQuery, injectQueryClient } from '../../query'
import { queryKeys } from '../../query-keys'
import {
  AssistResult,
  ChangeConversationStateInput,
  ConversationListFilters,
  ConversationViewer,
  IntercomAdmin,
  KnowledgeGap,
  ReplyInput,
  SupportAssistKind,
  SupportConversationContext,
  SupportConversationDetail,
  SupportConversationListRow,
  SupportConversationRow,
} from './support-conversations.models'

// The server's own maximum, asked for in full so the table sorts and filters in the browser. It is
// not a guarantee of the whole set: an environment with more matching conversations than this answers
// with its newest ones, and `truncated` says so rather than letting the rest go missing quietly.
const LIST_PAGE_SIZE = 200
// Only a page that is open polls; one environment every five seconds is one request per second at
// the widest fan-out.
const THREAD_POLL_MS = 5000
const LIST_POLL_MS = 30000
const DIRECTORY_USERS_PAGE_SIZE = 5000

type ListResponse = { list: SupportConversationRow[]; total: number }

/** An environment that had more matching conversations than one page holds. */
export type TruncatedList = { environment: SupportEnvironment; shown: number; total: number }

export type ConversationListPage = {
  rows: SupportConversationListRow[]
  truncated: TruncatedList[]
}

export type DirectoryUser = { id: number; firstName: string; lastName: string; email: string }

@Injectable({ providedIn: 'root' })
export class SupportConversationsService {
  private fanOutService = inject(EnvironmentFanOutService)
  private httpClient = inject(HttpClient)
  private query = injectQuery()
  private mutation = injectMutation()
  private queryClient = injectQueryClient()

  readonly environments = this.fanOutService.environments
  readonly isMultiEnvironment = this.environments.length > 1

  /**
   * The environment this app serves, for the pages whose subject is one of its own rows. Null in
   * Atlas, which serves none of them and reaches every environment from its own origin.
   */
  servingEnvironment(): SupportEnvironment | null {
    return this.environments.find(isSelfEnvironment) ?? null
  }

  environmentByKey(key: string | null): SupportEnvironment | null {
    if (key === null) {
      return this.environments.length === 1 ? this.environments[0] : null
    }
    return this.environments.find((environment) => environment.key === key) ?? null
  }

  list(filters: ConversationListFilters, environment: SupportEnvironment | null = null): Observable<FanOut<ConversationListPage>> {
    const params = {
      pageSize: LIST_PAGE_SIZE,
      sortField: 'intercomUpdatedAt',
      sortDirection: 'desc',
      states: filters.states,
      waitingOnUs: filters.waitingOnUs,
      ...(filters.userId === null ? {} : { userId: filters.userId }),
      ...(filters.callId === null ? {} : { callId: filters.callId }),
    }
    return this.fanOutService
      .fanOut<ListResponse>({
        path: '/api/intercom/conversations',
        params,
        queryKey: queryKeys.supportConversations.list(filters),
        refetchInterval: LIST_POLL_MS * this.environments.length,
        appliesTo: environment === null ? undefined : (candidate) => candidate.key === environment.key,
      })
      .pipe(
        map((results) =>
          toFanOut(results, (all) => ({
            rows: sortByUpdated(tagRows(all.map((result) => ({ ...result, data: result.data?.list ?? null })))),
            truncated: all.flatMap((result) => {
              const response = result.data
              if (response === null || response.total <= response.list.length) {
                return []
              }
              return [{ environment: result.environment, shown: response.list.length, total: response.total }]
            }),
          }))
        )
      )
  }

  waitingCount(): Observable<{ count: number; isChecking: boolean }> {
    return this.fanOutService
      .fanOut<ListResponse>({
        path: '/api/intercom/conversations',
        params: { pageSize: 1, waitingOnUs: true },
        queryKey: queryKeys.supportConversations.waitingCount,
        refetchInterval: LIST_POLL_MS * this.environments.length,
      })
      .pipe(
        map((results) => ({
          count: results.reduce((total, result) => total + (result.data?.total ?? 0), 0),
          isChecking: results.some((result) => result.state === 'checking'),
        }))
      )
  }

  viewers(): Observable<EnvironmentResult<ConversationViewer>[]> {
    return this.fanOutService.fanOut<ConversationViewer>({ path: '/api/intercom/viewer', queryKey: queryKeys.supportConversations.viewer })
  }

  admins(): Observable<EnvironmentResult<IntercomAdmin[]>[]> {
    return this.fanOutService.fanOut<IntercomAdmin[]>({ path: '/api/intercom/admins', queryKey: queryKeys.intercom.admins })
  }

  knowledgeGaps(): Observable<FanOut<(KnowledgeGap & { environment: SupportEnvironment })[]>> {
    return this.fanOutService
      .fanOut<{ list: KnowledgeGap[] }>({ path: '/api/intercom/knowledge-gaps', queryKey: queryKeys.supportConversations.knowledgeGaps })
      .pipe(map((results) => toFanOut(results, (all) => tagRows(all.map((result) => ({ ...result, data: result.data?.list ?? null }))))))
  }

  directoryUsers(environment: SupportEnvironment): Observable<EnvironmentResult<{ list: DirectoryUser[] }>[]> {
    return this.fanOutService.fanOut<{ list: DirectoryUser[] }>({
      path: '/api/users/support',
      params: { pageSize: DIRECTORY_USERS_PAGE_SIZE, sortField: 'email', sortDirection: 'asc', isActive: true },
      queryKey: ['support-conversations', 'directory-users'],
      appliesTo: (candidate) => candidate.key === environment.key,
    })
  }

  detail(environment: SupportEnvironment, intercomId: string) {
    return this.query({
      queryKey: this.environmentKey(environment, queryKeys.supportConversations.detail(intercomId)),
      queryFn: () => this.get<SupportConversationDetail>(environment, '/api/intercom/conversation', { id: intercomId }),
      refetchInterval: THREAD_POLL_MS,
      ...this.remotePolicy(environment),
    }).result$
  }

  context(environment: SupportEnvironment, intercomId: string) {
    return this.query({
      queryKey: this.environmentKey(environment, queryKeys.supportConversations.context(intercomId)),
      queryFn: () => this.get<SupportConversationContext>(environment, '/api/intercom/conversation-context', { id: intercomId }),
      staleTime: 30_000,
      ...this.remotePolicy(environment),
    }).result$
  }

  replyMutation(environment: SupportEnvironment) {
    return this.mutation({
      mutationFn: (input: ReplyInput) => this.post<SupportConversationRow>(environment, '/api/intercom/conversation-replies', input),
      onSuccess: (_, input) => this.invalidateConversation(environment, input.conversationId),
    })
  }

  changeStateMutation(environment: SupportEnvironment) {
    return this.mutation({
      mutationFn: (input: ChangeConversationStateInput) => this.post<SupportConversationRow>(environment, '/api/intercom/conversation-state', input),
      onSuccess: (_, input) => this.invalidateConversation(environment, input.conversationId),
    })
  }

  linkMutation(environment: SupportEnvironment) {
    return this.mutation({
      mutationFn: (input: { conversationId: string; callId: number | null }) =>
        this.post<SupportConversationRow>(environment, '/api/intercom/conversation-links', input),
      onSuccess: (_, input) => this.invalidateConversation(environment, input.conversationId),
    })
  }

  assistMutation(environment: SupportEnvironment) {
    return this.mutation({
      mutationFn: (input: { conversationId: string; kind: SupportAssistKind }) => this.post<AssistResult>(environment, '/api/intercom/assist', input),
      onSuccess: (_, input) => this.invalidateConversation(environment, input.conversationId),
    })
  }

  startConversationMutation(environment: SupportEnvironment) {
    return this.mutation({
      mutationFn: (input: { adminId: string; message: string; userId: number }) => this.post<unknown>(environment, '/api/intercom/start-conversation', input),
      onSuccess: () => this.invalidateLists(),
    })
  }

  private invalidateConversation(environment: SupportEnvironment, intercomId: string) {
    void this.queryClient.invalidateQueries({ queryKey: this.environmentKey(environment, queryKeys.supportConversations.detail(intercomId)) })
    void this.queryClient.invalidateQueries({ queryKey: this.environmentKey(environment, queryKeys.supportConversations.context(intercomId)) })
    this.invalidateLists()
  }

  /**
   * Every environment, because the lists are fan-out queries and the fan-out keys each one by the
   * environment it read. An unprefixed `['support-conversations']` matches none of them, so the inbox
   * and the waiting badge would sit on stale rows until the next poll.
   */
  private invalidateLists() {
    for (const environment of this.environments) {
      void this.queryClient.invalidateQueries({ queryKey: this.environmentKey(environment, queryKeys.supportConversations.base) })
    }
  }

  // Keyed like the fan-out's own queries, so the connection banner speaks for these reads too.
  private environmentKey(environment: SupportEnvironment, key: readonly unknown[]): unknown[] {
    return ['environment', environment.key, ...key]
  }

  // A remote environment answering 401 or 403 is a state to show, not a fault to retry.
  private remotePolicy(environment: SupportEnvironment) {
    return isSelfEnvironment(environment) ? {} : { retry: 0 }
  }

  private get<T>(environment: SupportEnvironment, path: string, params: Record<string, string>): Observable<T> {
    return this.httpClient.get<T>(`${environment.origin}${path}`, { params, withCredentials: !isSelfEnvironment(environment) })
  }

  private post<T>(environment: SupportEnvironment, path: string, body: unknown): Observable<T> {
    return this.httpClient.post<T>(`${environment.origin}${path}`, body, { withCredentials: !isSelfEnvironment(environment) })
  }
}

// Each environment answers newest first; the merged list has to as well.
export function sortByUpdated<T extends { intercomUpdatedAt: string; environment: SupportEnvironment; intercomId: string }>(rows: T[]): T[] {
  return rows.toSorted(
    (left, right) =>
      new Date(right.intercomUpdatedAt).getTime() - new Date(left.intercomUpdatedAt).getTime() ||
      left.environment.key.localeCompare(right.environment.key) ||
      left.intercomId.localeCompare(right.intercomId)
  )
}
